import React, { useState, useEffect, useMemo } from 'react';
import { Bell, Mail, MessageSquare, Volume2, X, Plus, Trash2, Play, Send, AlertTriangle, Zap, User, Smartphone, BellRing, AlertCircle, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../../lib/utils';
import {
  useSupportSettingsStore,
  NotificationRule,
  PersonalAgentPreference,
  EventTriggerType,
  DeliveryChannel,
  WarningSeverity,
  loadJson,
  saveJson,
  syncSettingFromServer,
  useActiveSupportSender
} from '../../../store/supportSettingsStore';
import { useAuthStore } from '../../../store/authStore';

export type { EventTriggerType, DeliveryChannel, WarningSeverity, NotificationRule, PersonalAgentPreference };

const TRIGGER_LABELS: Record<EventTriggerType, { label: string; group: 'notification' | 'warning' | 'approval'; defaultSeverity: WarningSeverity }> = {
  ticket_created: { label: 'Ticket Created / Received', group: 'notification', defaultSeverity: 'info' },
  ticket_assigned: { label: 'Ticket Assigned to Agent', group: 'notification', defaultSeverity: 'info' },
  status_changed: { label: 'Ticket Status Transitioned', group: 'notification', defaultSeverity: 'info' },
  note_added_public: { label: 'Public Note Added', group: 'notification', defaultSeverity: 'info' },
  note_added_internal: { label: 'Internal Staff Note Added', group: 'notification', defaultSeverity: 'info' },
  customer_replied: { label: 'Customer Replied / New Message', group: 'notification', defaultSeverity: 'info' },
  sla_warning: { label: 'SLA Warning Threshold (<15m / 75%)', group: 'warning', defaultSeverity: 'critical' },
  sla_breached: { label: 'SLA Target Breached Escalation', group: 'warning', defaultSeverity: 'critical' },
  unassigned_warning: { label: 'Unassigned Queue Warning (>30m)', group: 'warning', defaultSeverity: 'urgent' },
  inactivity_warning: { label: 'Stale / Inactive Ticket Warning (>48h)', group: 'warning', defaultSeverity: 'low' },
  urgent_priority_warning: { label: 'High Priority P1 Urgent Alert', group: 'warning', defaultSeverity: 'critical' },
  approval_requested: { label: 'Approval Sign-Off Gate Required', group: 'approval', defaultSeverity: 'urgent' },
  ticket_resolved: { label: 'Ticket Resolved Receipt', group: 'notification', defaultSeverity: 'info' },
  ticket_closed: { label: 'Ticket Closed & Archived', group: 'notification', defaultSeverity: 'info' },
  vip_customer_activity: { label: 'VIP Customer Activity', group: 'approval', defaultSeverity: 'urgent' },
  asset_warranty_expiring: { label: 'Hardware Warranty Expiring Warning', group: 'warning', defaultSeverity: 'low' },
};

const SEVERITY_CONFIG: Record<WarningSeverity, { label: string; badge: string; border: string; dot: string; icon: any }> = {
  info: { label: 'Info Notification', badge: 'bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300 border-sky-300 dark:border-sky-800', border: 'border-l-sky-500', dot: 'bg-sky-500', icon: Bell },
  low: { label: 'Low Warning', badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border-amber-300 dark:border-amber-800', border: 'border-l-amber-500', dot: 'bg-amber-500', icon: AlertCircle },
  warning: { label: 'Warning Alert', badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border-amber-300 dark:border-amber-800', border: 'border-l-amber-500', dot: 'bg-amber-500', icon: AlertTriangle },
  urgent: { label: 'Urgent Warning', badge: 'bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300 border-orange-300 dark:border-orange-800', border: 'border-l-orange-500', dot: 'bg-orange-500', icon: AlertTriangle },
  critical: { label: 'Critical Alert', badge: 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border-rose-300 dark:border-rose-800', border: 'border-l-rose-500', dot: 'bg-rose-500', icon: ShieldAlert },
};

export function NotificationsView() {
  const activeSender = useActiveSupportSender();
  const {
    notificationRules: storeRules,
    fetchNotificationRules,
    saveNotificationRule,
    deleteNotificationRule,
    agentPreference,
    fetchAgentPreference,
    saveAgentPreference,
    isLoadingNotificationRules,
    ticketTypes: storeTicketTypes,
    fetchTicketTypes,
    queues: storeQueues,
    fetchQueues,
    agents: storeAgents,
    fetchAgents
  } = useSupportSettingsStore();

  const { user: currentUser } = useAuthStore();

  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [selectedRuleId, setSelectedRuleId] = useState<string>('notif-1');
  const [activeTab, setActiveTab] = useState<'system_rules' | 'personal_preferences' | 'simulator'>('system_rules');
  const [ruleCategoryFilter, setRuleCategoryFilter] = useState<'all' | 'warnings' | 'notifications' | 'approvals'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTriggerFilter, setSelectedTriggerFilter] = useState<string>('all');
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);

  useEffect(() => {
    void fetchNotificationRules();
    void fetchAgentPreference();
    void fetchTicketTypes();
    void fetchQueues();
    void fetchAgents();
  }, [fetchNotificationRules, fetchAgentPreference, fetchTicketTypes, fetchQueues, fetchAgents]);

  useEffect(() => {
    if (storeRules && storeRules.length > 0) {
      setRules(storeRules);
      if (!selectedRuleId || !storeRules.some((r) => r.id === selectedRuleId)) {
        setSelectedRuleId(storeRules[0].id);
      }
    }
  }, [storeRules, selectedRuleId]);

  const activeAgentEmail = currentUser?.email || 'girikumaran@trustybytes.in';

  // Personal Notifications Preferences State
  const defaultPrefs = {
    'notif-1': { email: true, in_app: true, sound: false, chat_webhook: true },
    'notif-2': { email: true, in_app: true, sound: true, chat_webhook: true },
    'notif-3': { email: true, in_app: true, sound: true, chat_webhook: true },
    'notif-4': { email: true, in_app: true, sound: true, chat_webhook: true },
    'notif-5': { email: false, in_app: true, sound: true, chat_webhook: true },
    'notif-6': { email: true, in_app: true, sound: false, chat_webhook: false },
    'notif-7': { email: true, in_app: true, sound: true, chat_webhook: true },
    'notif-8': { email: true, in_app: true, sound: true, chat_webhook: false },
    'notif-9': { email: true, in_app: true, sound: true, chat_webhook: true },
    'notif-10': { email: true, in_app: true, sound: false, chat_webhook: false },
  };

  const [agentPreferences, setAgentPreferences] = useState<Record<string, { email: boolean; in_app: boolean; sound: boolean; chat_webhook: boolean }>>(() => {
    return loadJson('teamone_personal_notif_prefs', defaultPrefs);
  });

  useEffect(() => {
    void syncSettingFromServer('teamone_personal_notif_prefs', defaultPrefs).then((prefs) => {
      if (prefs) setAgentPreferences(prefs);
    });
  }, []);

  // Simulator state
  const [simPreviewChannel, setSimPreviewChannel] = useState<'email' | 'in_app' | 'chat_webhook' | 'sms'>('email');

  // Dynamic lists from store
  const availableTicketTypes = useMemo(() => {
    if (storeTicketTypes && storeTicketTypes.length > 0) {
      return storeTicketTypes.map(t => t.name);
    }
    return ['Incident', 'Service Request', 'Access Request', 'Feature Request', 'Major Incident', 'Problem'];
  }, [storeTicketTypes]);

  const availableQueues = useMemo(() => {
    if (storeQueues && storeQueues.length > 0) {
      return storeQueues.map(q => q.label || q.queue_label || q.key || 'Support Team');
    }
    return ['Support Team', 'IT Asset Management', 'L1 Helpdesk', 'Engineering Escalations'];
  }, [storeQueues]);

  // Form State for creating a new notification or warning rule
  const [newRuleForm, setNewRuleForm] = useState<{
    name: string;
    eventTrigger: EventTriggerType;
    description: string;
    scope: 'global' | 'personal';
    warningSeverity: WarningSeverity;
    warningThresholdValue: number;
    warningThresholdUnit: 'minutes' | 'hours' | 'percent_sla';
    autoEscalate: boolean;
    channels: NotificationRule['channels'];
    selectedTicketTypes: string[];
    selectedQueues: string[];
    selectedPriorities: string[];
    subject: string;
    bodyText: string;
    soundChime: 'standard_chime' | 'urgent_siren' | 'subtle_ping';
  }>({
    name: '',
    eventTrigger: 'sla_warning',
    description: '',
    scope: 'global',
    warningSeverity: 'critical',
    warningThresholdValue: 15,
    warningThresholdUnit: 'minutes',
    autoEscalate: true,
    channels: { email: true, in_app: true, sound: true, chat_webhook: true, sms: false },
    selectedTicketTypes: ['Incident'],
    selectedQueues: ['Support Team'],
    selectedPriorities: ['Urgent', 'High'],
    subject: '⚠️ CRITICAL WARNING: TB#{ticket_id} ({subject})',
    bodyText: 'ATTENTION: Ticket TB#{ticket_id} ({subject}) in queue {queue_name} requires immediate action.\nAssignee: {assignee_name}\nPriority: {priority}\nStatus: {status}',
    soundChime: 'urgent_siren',
  });

  const saveRules = (updated: NotificationRule[]) => {
    setRules(updated);
    localStorage.setItem('teamone_notification_rules', JSON.stringify(updated));
    const modified = updated.find(r => r.id === (currentRule?.id || selectedRuleId));
    if (modified) {
      void saveNotificationRule(modified);
    }
  };

  const currentRule = useMemo(() => {
    return rules.find(r => r.id === selectedRuleId) || rules[0] || {
      id: 'notif-1',
      name: 'Default Notification',
      eventTrigger: 'ticket_created',
      description: 'System alert',
      isEnabled: true,
      scope: 'global',
      warningSeverity: 'info',
      channels: { email: true, in_app: true, sound: false, chat_webhook: false, sms: false },
      criteria: { ticketTypes: ['Incident'], queues: ['Support Team'], priorities: ['Urgent'], categories: [] },
      recipients: { requester: true, assignedAgent: true, queueMembers: false, reportingManager: false, ticketWatchers: false },
      template: { subject: '[TB#{ticket_id}] Ticket Received', bodyText: 'Ticket received.' }
    } as NotificationRule;
  }, [rules, selectedRuleId]);

  const filteredRules = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return rules.filter(r => {
      const matchesSearch = !q || r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || r.template?.subject?.toLowerCase().includes(q);
      const matchesTrigger = selectedTriggerFilter === 'all' || r.eventTrigger === selectedTriggerFilter;
      
      let matchesCategory = true;
      const triggerInfo = TRIGGER_LABELS[r.eventTrigger] || { group: 'notification' };
      if (ruleCategoryFilter === 'warnings') {
        matchesCategory = triggerInfo.group === 'warning' || r.warningSeverity === 'critical' || r.warningSeverity === 'urgent' || r.eventTrigger.includes('warning') || r.eventTrigger.includes('breached');
      } else if (ruleCategoryFilter === 'notifications') {
        matchesCategory = triggerInfo.group === 'notification';
      } else if (ruleCategoryFilter === 'approvals') {
        matchesCategory = triggerInfo.group === 'approval' || r.eventTrigger === 'approval_requested';
      }

      return matchesSearch && matchesTrigger && matchesCategory;
    });
  }, [rules, searchQuery, selectedTriggerFilter, ruleCategoryFilter]);

  // Handler: Toggle rule active state
  const handleToggleRuleActive = (ruleId: string) => {
    const updated = rules.map(r => r.id === ruleId ? { ...r, isEnabled: !r.isEnabled } : r);
    saveRules(updated);
    toast.success('Notification & Warning rule status updated');
  };

  // Handler: Update current rule field
  const handleUpdateCurrentRule = (updates: Partial<NotificationRule>) => {
    const updated = rules.map(r => {
      if (r.id === currentRule.id) {
        return { ...r, ...updates };
      }
      return r;
    });
    saveRules(updated);
  };

  // Handler: Toggle individual channel on selected rule
  const handleToggleChannel = (channelKey: keyof NotificationRule['channels']) => {
    const updated = rules.map(r => {
      if (r.id === currentRule.id) {
        return {
          ...r,
          channels: {
            ...r.channels,
            [channelKey]: !r.channels[channelKey],
          }
        };
      }
      return r;
    });
    saveRules(updated);
    toast.success(`Channel ${channelKey.toUpperCase()} toggled`);
  };

  // Handler: Add New Notification / Warning Rule
  const handleAddRuleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRuleForm.name) return;

    const newRule: NotificationRule = {
      id: `notif-${Date.now()}`,
      name: newRuleForm.name,
      eventTrigger: newRuleForm.eventTrigger,
      description: newRuleForm.description || 'Custom notification alert trigger and warning rule.',
      isEnabled: true,
      scope: newRuleForm.scope,
      ownerAgentEmail: newRuleForm.scope === 'personal' ? activeAgentEmail : undefined,
      warningSeverity: newRuleForm.warningSeverity,
      warningThresholdValue: newRuleForm.warningThresholdValue,
      warningThresholdUnit: newRuleForm.warningThresholdUnit,
      autoEscalate: newRuleForm.autoEscalate,
      channels: newRuleForm.channels,
      criteria: {
        ticketTypes: newRuleForm.selectedTicketTypes.length > 0 ? newRuleForm.selectedTicketTypes : availableTicketTypes.slice(0, 2),
        queues: newRuleForm.selectedQueues.length > 0 ? newRuleForm.selectedQueues : availableQueues.slice(0, 1),
        priorities: newRuleForm.selectedPriorities.length > 0 ? newRuleForm.selectedPriorities : ['Urgent', 'High'],
        categories: ['All Categories'],
        workingHoursOnly: false,
      },
      recipients: {
        requester: newRuleForm.eventTrigger === 'ticket_created' || newRuleForm.eventTrigger === 'ticket_resolved',
        assignedAgent: true,
        queueMembers: newRuleForm.warningSeverity === 'critical' || newRuleForm.warningSeverity === 'urgent',
        reportingManager: newRuleForm.autoEscalate || newRuleForm.warningSeverity === 'critical',
        ticketWatchers: true,
      },
      template: {
        subject: newRuleForm.subject,
        bodyText: newRuleForm.bodyText,
        chatCardTitle: newRuleForm.name,
        soundChime: newRuleForm.soundChime,
      }
    };

    const updated = [...rules, newRule];
    saveRules(updated);
    setSelectedRuleId(newRule.id);
    setShowAddRuleModal(false);
    toast.success(`Created Rule "${newRule.name}"`);
  };

  // Handler: Delete Notification
  const handleDeleteRule = (ruleId: string, name: string) => {
    if (rules.length <= 1) {
      toast.error('You must maintain at least 1 notification rule');
      return;
    }
    const updated = rules.filter(r => r.id !== ruleId);
    saveRules(updated);
    void deleteNotificationRule(ruleId);
    if (selectedRuleId === ruleId) {
      setSelectedRuleId(updated[0]?.id || '');
    }
    toast.success(`Rule "${name}" removed`);
  };

  // Handler: Personal Preference Channel Toggle
  const handleTogglePersonalPref = (ruleId: string, channel: 'email' | 'in_app' | 'sound' | 'chat_webhook') => {
    const current = agentPreferences[ruleId] || { email: true, in_app: true, sound: true, chat_webhook: true };
    const updated = {
      ...agentPreferences,
      [ruleId]: {
        ...current,
        [channel]: !current[channel],
      }
    };
    setAgentPreferences(updated);
    saveJson('teamone_personal_notif_prefs', updated);
    void saveAgentPreference({
      agentEmail: activeAgentEmail,
      agentName: currentUser?.name || 'Agent',
      subscribedRuleIds: Object.keys(updated).filter(k => Object.values(updated[k]).some(v => v)),
      optedOutChannelMap: updated
    });
    toast.success('Personal notification preference updated');
  };

  // Audio Chime Simulator
  const playSampleSound = (soundType: string = 'standard_chime') => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);

      if (soundType === 'urgent_siren') {
        osc.frequency.setValueAtTime(880, audioCtx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(440, audioCtx.currentTime + 0.35);
        gain.gain.setValueAtTime(0.25, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.35);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.35);
      } else if (soundType === 'subtle_ping') {
        osc.frequency.setValueAtTime(1046.5, audioCtx.currentTime); // C6
        gain.gain.setValueAtTime(0.12, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.15);
      } else {
        osc.frequency.setValueAtTime(587.33, audioCtx.currentTime); // D5
        osc.frequency.setValueAtTime(880, audioCtx.currentTime + 0.1); // A5
        gain.gain.setValueAtTime(0.18, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.25);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.25);
      }
      toast.success(`Playing sound: ${soundType.replace(/_/g, ' ')}`);
    } catch (e) {
      toast.success('Sound chime simulated');
    }
  };

  // Insert token into body text helper
  const handleInsertToken = (token: string) => {
    const currentText = currentRule.template.bodyText || '';
    const updatedText = `${currentText} {${token}}`;
    handleUpdateCurrentRule({
      template: {
        ...currentRule.template,
        bodyText: updatedText
      }
    });
    toast.success(`Inserted token {${token}}`);
  };

  const isWarningTrigger = useMemo(() => {
    const trigger = currentRule.eventTrigger;
    return trigger === 'sla_warning' || trigger === 'sla_breached' || trigger === 'unassigned_warning' || trigger === 'inactivity_warning' || trigger === 'urgent_priority_warning' || trigger === 'asset_warranty_expiring';
  }, [currentRule.eventTrigger]);

  const severityCfg = SEVERITY_CONFIG[currentRule.warningSeverity || (isWarningTrigger ? 'urgent' : 'info')];

  return (
    <div className="space-y-6">
      {/* 1. TOP HEADER BANNER */}
      <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border pb-4">
          <div>
            <h2 className="font-display text-section-title text-foreground flex items-center gap-2">
              <Bell className="size-5 text-primary" /> Notifications, Warnings &amp; Escalation Rules
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
              Configure automated notification triggers, SLA breach threshold warnings, stale ticket reminders, and multi-channel delivery rules across Email, In-App Push, Siren Alerts, Chat Webhooks, and SMS.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setNewRuleForm({
                  name: '',
                  eventTrigger: 'sla_warning',
                  description: '',
                  scope: 'global',
                  warningSeverity: 'critical',
                  warningThresholdValue: 15,
                  warningThresholdUnit: 'minutes',
                  autoEscalate: true,
                  channels: { email: true, in_app: true, sound: true, chat_webhook: true, sms: false },
                  selectedTicketTypes: availableTicketTypes.slice(0, 2),
                  selectedQueues: availableQueues.slice(0, 1),
                  selectedPriorities: ['Urgent', 'High'],
                  subject: '⚠️ CRITICAL WARNING: TB#{ticket_id} ({subject})',
                  bodyText: 'ATTENTION: Ticket TB#{ticket_id} ({subject}) in queue {queue_name} requires immediate action.\nAssignee: {assignee_name}\nPriority: {priority}',
                  soundChime: 'urgent_siren',
                });
                setShowAddRuleModal(true);
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-foreground text-background text-xs font-semibold shadow-xs hover:opacity-90 cursor-pointer"
            >
              <Plus className="size-3.5" /> New Notification / Warning Rule
            </button>
          </div>
        </div>

        {/* TOP TAB SWITCHER */}
        <div className="flex items-center justify-between border-t border-border pt-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setActiveTab('system_rules')}
              className={cn(
                "px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer",
                activeTab === 'system_rules'
                  ? "bg-secondary text-foreground border border-border/80 shadow-xs font-semibold ring-1 ring-border/50"
                  : "bg-background text-muted-foreground hover:text-foreground border border-border"
              )}
            >
              <Zap className="size-3.5 text-amber-500" /> System &amp; Warning Rules ({rules.length})
            </button>
            <button
              onClick={() => setActiveTab('personal_preferences')}
              className={cn(
                "px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer",
                activeTab === 'personal_preferences'
                  ? "bg-secondary text-foreground border border-border/80 shadow-xs font-semibold ring-1 ring-border/50"
                  : "bg-background text-muted-foreground hover:text-foreground border border-border"
              )}
            >
              <User className="size-3.5 text-sky-500" /> Personal Subscriptions ("My Account")
            </button>
            <button
              onClick={() => setActiveTab('simulator')}
              className={cn(
                "px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer text-indigo-600 dark:text-indigo-400",
                activeTab === 'simulator'
                  ? "bg-indigo-600 text-white font-semibold shadow-xs"
                  : "bg-background text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900"
              )}
            >
              <Play className="size-3.5" /> Multi-Channel Siren &amp; Dispatch Simulator
            </button>
          </div>

          <div className="text-xs text-muted-foreground hidden md:flex items-center gap-3">
            <span className="flex items-center gap-1 text-xs"><span className="size-2 rounded-full bg-rose-500"></span> Warnings &amp; SLA</span>
            <span className="flex items-center gap-1 text-xs"><span className="size-2 rounded-full bg-sky-500"></span> Notifications</span>
          </div>
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* TAB 1: SYSTEM & EVENT NOTIFICATION RULES */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {activeTab === 'system_rules' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* LEFT LIST: NOTIFICATION & WARNING RULES */}
          <div className="lg:col-span-4 rounded-xl border border-border bg-card p-4 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Configured Rules ({filteredRules.length})
              </h3>
              <div className="flex items-center gap-1 text-xs">
                <button
                  type="button"
                  onClick={() => setRuleCategoryFilter('all')}
                  className={cn("px-2 py-0.5 rounded cursor-pointer font-semibold", ruleCategoryFilter === 'all' ? "bg-foreground text-background" : "bg-muted text-muted-foreground")}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setRuleCategoryFilter('warnings')}
                  className={cn("px-2 py-0.5 rounded cursor-pointer font-semibold", ruleCategoryFilter === 'warnings' ? "bg-rose-600 text-white" : "bg-muted text-muted-foreground")}
                >
                  Warnings
                </button>
                <button
                  type="button"
                  onClick={() => setRuleCategoryFilter('notifications')}
                  className={cn("px-2 py-0.5 rounded cursor-pointer font-semibold", ruleCategoryFilter === 'notifications' ? "bg-sky-600 text-white" : "bg-muted text-muted-foreground")}
                >
                  Alerts
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <input
                type="text"
                placeholder="Search rule title or trigger..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-1.5 bg-background border border-input rounded-md text-xs"
              />

              <select
                value={selectedTriggerFilter}
                onChange={(e) => setSelectedTriggerFilter(e.target.value)}
                className="w-full px-2.5 py-1.5 bg-background border border-input rounded-md text-xs font-semibold"
              >
                <option value="all">All Event &amp; Warning Triggers</option>
                <option value="sla_warning">⚠️ SLA Warning Threshold</option>
                <option value="sla_breached">🚨 SLA Target Breached</option>
                <option value="unassigned_warning">⚠️ Unassigned Queue Warning</option>
                <option value="inactivity_warning">⏳ Stale / Inactivity Warning</option>
                <option value="urgent_priority_warning">🔥 High Priority P1 Alert</option>
                <option value="ticket_created">📩 Ticket Created / Received</option>
                <option value="ticket_assigned">👤 Ticket Assigned to Agent</option>
                <option value="customer_replied">💬 Customer Replied</option>
                <option value="approval_requested">📋 Approval Requested</option>
                <option value="ticket_resolved">✅ Ticket Resolved</option>
              </select>
            </div>

            <div className="space-y-1.5 max-h-[620px] overflow-y-auto pr-1">
              {filteredRules.map(rule => {
                const isSelected = selectedRuleId === rule.id;
                const isWarning = rule.eventTrigger.includes('warning') || rule.eventTrigger.includes('breached') || rule.warningSeverity === 'critical' || rule.warningSeverity === 'urgent';
                const severity = SEVERITY_CONFIG[rule.warningSeverity || (isWarning ? 'urgent' : 'info')];

                return (
                  <div
                    key={rule.id}
                    onClick={() => setSelectedRuleId(rule.id)}
                    className={cn(
                      "p-3 rounded-lg border text-xs transition-all flex flex-col gap-1.5 cursor-pointer border-l-4",
                      severity.border,
                      isSelected
                        ? "bg-muted/80 border-primary font-semibold shadow-xs"
                        : "bg-background border-border hover:bg-muted/40"
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 truncate">
                        {isWarning && <AlertTriangle className="size-3 text-rose-500 flex-shrink-0" />}
                        <span className="truncate text-foreground font-semibold">{rule.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleRuleActive(rule.id);
                        }}
                        className={cn(
                          "size-2.5 rounded-full flex-shrink-0 cursor-pointer",
                          rule.isEnabled ? "bg-emerald-500 ring-2 ring-emerald-500/20" : "bg-muted-foreground/40"
                        )}
                        title={rule.isEnabled ? 'Active (Click to pause)' : 'Paused (Click to activate)'}
                      />
                    </div>

                    <p className="text-xs text-muted-foreground line-clamp-1 font-normal">
                      {rule.description}
                    </p>

                    <div className="flex items-center justify-between text-xs text-muted-foreground font-mono pt-1 border-t border-border/40">
                      <span className={cn("px-1.5 py-0.2 rounded font-semibold text-xs border", severity.badge)}>
                        {TRIGGER_LABELS[rule.eventTrigger]?.label || rule.eventTrigger}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {rule.channels.email && <span title="Email Active"><Mail className="size-3 text-sky-500" /></span>}
                        {rule.channels.in_app && <span title="In-App Active"><Bell className="size-3 text-indigo-500" /></span>}
                        {rule.channels.sound && <span title="Sound Chime Active"><Volume2 className="size-3 text-amber-500" /></span>}
                        {rule.channels.chat_webhook && <span title="Chat Webhook Active"><MessageSquare className="size-3 text-purple-500" /></span>}
                        {rule.channels.sms && <span title="SMS Active"><Smartphone className="size-3 text-emerald-500" /></span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT DETAIL PANE: NOTIFICATION & WARNING CONFIGURATION */}
          <div className="lg:col-span-8 rounded-xl border border-border bg-card shadow-xs overflow-hidden space-y-5 p-5">
            <div className="border-b border-border pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-mono font-semibold uppercase px-2 py-0.5 rounded bg-muted border border-border text-foreground">
                    Trigger: {currentRule.eventTrigger}
                  </span>
                  <span className={cn("text-xs font-semibold px-2 py-0.5 rounded border", severityCfg.badge)}>
                    Severity: {currentRule.warningSeverity?.toUpperCase() || (isWarningTrigger ? 'URGENT WARNING' : 'INFO')}
                  </span>
                  <span className={cn(
                    "text-xs font-semibold px-2 py-0.5 rounded border",
                    currentRule.isEnabled ? "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-muted text-muted-foreground"
                  )}>
                    {currentRule.isEnabled ? 'Live & Active' : 'Rule Paused'}
                  </span>
                </div>
                <h3 className="font-display text-card-title text-foreground mt-1.5 flex items-center gap-2">
                  {isWarningTrigger ? <AlertTriangle className="size-4 text-rose-600" /> : <Bell className="size-4 text-primary" />}
                  {currentRule.name}
                </h3>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleToggleRuleActive(currentRule.id)}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted cursor-pointer"
                >
                  {currentRule.isEnabled ? 'Pause Rule' : 'Activate Rule'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteRule(currentRule.id, currentRule.name)}
                  className="p-1.5 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 cursor-pointer"
                  title="Delete Rule"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>

            {/* 1. WARNING & THRESHOLD CONFIGURATION (Rendered prominently for warnings) */}
            <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
              <div className="flex items-center justify-between border-b border-border/60 pb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground flex items-center gap-1.5">
                  <ShieldAlert className="size-3.5 text-rose-600" /> Warning Threshold &amp; Escalation Rule Settings
                </h4>
                <span className="text-xs text-muted-foreground font-mono">Real-Time Evaluation Engine</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <label className="block font-semibold text-muted-foreground mb-1">Warning Severity Level</label>
                  <select
                    value={currentRule.warningSeverity || (isWarningTrigger ? 'urgent' : 'info')}
                    onChange={(e) => handleUpdateCurrentRule({ warningSeverity: e.target.value as WarningSeverity })}
                    className="w-full px-2.5 py-1.5 bg-background border border-input rounded-md text-xs font-semibold"
                  >
                    <option value="info">Info / Standard Notification</option>
                    <option value="low">Low Warning (Amber)</option>
                    <option value="urgent">Urgent Warning (Orange)</option>
                    <option value="critical">Critical Warning / Siren (Red)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-muted-foreground mb-1">Threshold Time Window</label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={1}
                      max={720}
                      value={currentRule.warningThresholdValue || 15}
                      onChange={(e) => handleUpdateCurrentRule({ warningThresholdValue: parseInt(e.target.value) || 15 })}
                      className="w-20 px-2 py-1.5 bg-background border border-input rounded-md text-xs font-mono font-semibold"
                    />
                    <select
                      value={currentRule.warningThresholdUnit || 'minutes'}
                      onChange={(e) => handleUpdateCurrentRule({ warningThresholdUnit: e.target.value as any })}
                      className="flex-1 px-2 py-1.5 bg-background border border-input rounded-md text-xs font-semibold"
                    >
                      <option value="minutes">Minutes before breach</option>
                      <option value="hours">Hours inactive</option>
                      <option value="percent_sla">% of SLA elapsed</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col justify-end">
                  <label className="flex items-center gap-2 p-2 rounded-lg border border-border bg-background cursor-pointer text-xs font-semibold">
                    <input
                      type="checkbox"
                      checked={!!currentRule.autoEscalate}
                      onChange={(e) => handleUpdateCurrentRule({ autoEscalate: e.target.checked })}
                      className="rounded border-input text-rose-600"
                    />
                    <span>Auto-escalate to Leads on Warning</span>
                  </label>
                </div>
              </div>
            </div>

            {/* 2. DELIVERY CHANNELS TOGGLE BAR */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Active Multi-Channel Dispatch
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                {[
                  { key: 'email' as const, label: 'Email Dispatch', icon: Mail, color: 'text-sky-500' },
                  { key: 'in_app' as const, label: 'In-App Toast Banner', icon: Bell, color: 'text-indigo-500' },
                  { key: 'sound' as const, label: 'Sound Siren Chime', icon: Volume2, color: 'text-amber-500' },
                  { key: 'chat_webhook' as const, label: 'Chat Space Webhook', icon: MessageSquare, color: 'text-purple-500' },
                  { key: 'sms' as const, label: 'SMS / Mobile Push', icon: Smartphone, color: 'text-emerald-500' },
                ].map(item => {
                  const isChanActive = currentRule.channels[item.key];
                  const IconComp = item.icon;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => handleToggleChannel(item.key)}
                      className={cn(
                        "p-3 rounded-lg border text-xs flex flex-col items-center gap-1.5 text-center transition-all cursor-pointer",
                        isChanActive
                          ? "bg-card border-foreground/50 shadow-xs font-semibold ring-1 ring-border"
                          : "bg-muted/20 border-border text-muted-foreground opacity-50 hover:opacity-80"
                      )}
                    >
                      <IconComp className={cn("size-4", isChanActive ? item.color : "text-muted-foreground")} />
                      <span className="text-xs font-semibold">{item.label}</span>
                      <span className={cn(
                        "text-xs font-mono px-1.5 py-0.2 rounded font-semibold",
                        isChanActive ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground"
                      )}>
                        {isChanActive ? 'ENABLED' : 'OFF'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 3. RECIPIENT MATRIX */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Audience &amp; Target Recipients
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {[
                  { key: 'requester' as const, label: 'Ticket Requester (Customer)' },
                  { key: 'assignedAgent' as const, label: 'Assigned Agent' },
                  { key: 'queueMembers' as const, label: 'Entire Queue / Team Members' },
                  { key: 'reportingManager' as const, label: 'Reporting Manager & Leads' },
                  { key: 'ticketWatchers' as const, label: 'Ticket CC / Watchers' },
                ].map(item => {
                  const isChecked = currentRule.recipients[item.key];
                  return (
                    <label
                      key={item.key}
                      className={cn(
                        "p-2.5 rounded-lg border flex items-center gap-2 cursor-pointer transition-all",
                        isChecked ? "bg-card border-primary/50 shadow-xs font-semibold text-foreground" : "bg-muted/20 border-border text-muted-foreground"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          handleUpdateCurrentRule({
                            recipients: { ...currentRule.recipients, [item.key]: e.target.checked }
                          });
                        }}
                        className="rounded border-input text-primary"
                      />
                      <span className="text-xs">{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* 4. GRANULAR CRITERIA TABLE (DYNAMICALLY BOUND) */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Trigger Criteria Filters (Dynamically Bound to DB)
              </label>
              <div className="p-3.5 rounded-lg bg-muted/30 border border-border grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <span className="font-semibold text-muted-foreground block mb-1">Ticket Types</span>
                  <div className="flex flex-wrap gap-1">
                    {availableTicketTypes.map(t => {
                      const isSelected = currentRule.criteria.ticketTypes.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => {
                            const current = currentRule.criteria.ticketTypes || [];
                            const next = isSelected ? current.filter(x => x !== t) : [...current, t];
                            handleUpdateCurrentRule({
                              criteria: { ...currentRule.criteria, ticketTypes: next }
                            });
                          }}
                          className={cn(
                            "px-1.5 py-0.5 rounded text-xs font-mono cursor-pointer border transition-all",
                            isSelected ? "bg-primary text-primary-foreground font-semibold border-transparent" : "bg-card text-muted-foreground border-border"
                          )}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <span className="font-semibold text-muted-foreground block mb-1">Applicable Queues</span>
                  <div className="flex flex-wrap gap-1">
                    {availableQueues.map(q => {
                      const isSelected = currentRule.criteria.queues.includes(q);
                      return (
                        <button
                          key={q}
                          type="button"
                          onClick={() => {
                            const current = currentRule.criteria.queues || [];
                            const next = isSelected ? current.filter(x => x !== q) : [...current, q];
                            handleUpdateCurrentRule({
                              criteria: { ...currentRule.criteria, queues: next }
                            });
                          }}
                          className={cn(
                            "px-1.5 py-0.5 rounded text-xs font-mono cursor-pointer border transition-all",
                            isSelected ? "bg-foreground text-background font-semibold border-transparent" : "bg-card text-muted-foreground border-border"
                          )}
                        >
                          {q}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <span className="font-semibold text-muted-foreground block mb-1">Priority Levels</span>
                  <div className="flex flex-wrap gap-1">
                    {['Urgent', 'High', 'Medium', 'Low'].map(p => {
                      const isSelected = currentRule.criteria.priorities.includes(p);
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() => {
                            const current = currentRule.criteria.priorities || [];
                            const next = isSelected ? current.filter(x => x !== p) : [...current, p];
                            handleUpdateCurrentRule({
                              criteria: { ...currentRule.criteria, priorities: next }
                            });
                          }}
                          className={cn(
                            "px-1.5 py-0.5 rounded text-xs font-mono font-semibold cursor-pointer border transition-all",
                            isSelected ? "bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/50 dark:text-rose-300" : "bg-card text-muted-foreground border-border"
                          )}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* 5. TEMPLATE & BODY MESSAGE */}
            <div className="space-y-3 text-xs">
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notification Template &amp; Formatting
              </label>

              <div>
                <label className="block font-semibold text-muted-foreground mb-1">Subject / Header Template</label>
                <input
                  type="text"
                  value={currentRule.template.subject}
                  onChange={(e) => {
                    handleUpdateCurrentRule({
                      template: { ...currentRule.template, subject: e.target.value }
                    });
                  }}
                  className="w-full px-3 py-1.5 bg-background border border-input rounded-md font-mono text-xs font-semibold"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="font-semibold text-muted-foreground">Body Text / Markdown Content</label>
                  <span className="text-xs text-muted-foreground">Click chip below to append variable token</span>
                </div>
                <textarea
                  rows={4}
                  value={currentRule.template.bodyText}
                  onChange={(e) => {
                    handleUpdateCurrentRule({
                      template: { ...currentRule.template, bodyText: e.target.value }
                    });
                  }}
                  className="w-full px-3 py-1.5 bg-background border border-input rounded-md font-mono text-xs"
                />

                {/* DYNAMIC TOKEN CHIPS */}
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {[
                    'ticket_id', 'subject', 'requester_name', 'assignee_name', 'priority',
                    'queue_name', 'sla_target_time', 'sla_time_remaining', 'status', 'company_name'
                  ].map(token => (
                    <button
                      key={token}
                      type="button"
                      onClick={() => handleInsertToken(token)}
                      className="px-1.5 py-0.5 rounded bg-muted hover:bg-muted/80 text-xs font-mono text-foreground border border-border cursor-pointer transition-all"
                    >
                      +{`{${token}}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* SOUND ALERT SELECTOR */}
              <div className="flex items-center gap-3 pt-2">
                <span className="font-semibold text-muted-foreground">Sound Alert Chime:</span>
                <select
                  value={currentRule.template.soundChime || (isWarningTrigger ? 'urgent_siren' : 'standard_chime')}
                  onChange={(e) => {
                    handleUpdateCurrentRule({
                      template: { ...currentRule.template, soundChime: e.target.value as any }
                    });
                  }}
                  className="px-2.5 py-1 bg-background border border-input rounded-md text-xs font-semibold"
                >
                  <option value="standard_chime">Standard Helpdesk Chime</option>
                  <option value="urgent_siren">🚨 Urgent P1 Siren Alert</option>
                  <option value="subtle_ping">Subtle Ping</option>
                </select>
                <button
                  type="button"
                  onClick={() => playSampleSound(currentRule.template.soundChime || (isWarningTrigger ? 'urgent_siren' : 'standard_chime'))}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-border bg-muted hover:bg-muted/80 text-xs font-semibold cursor-pointer"
                >
                  <Volume2 className="size-3 text-amber-500" /> Test Audio Alert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* TAB 2: PERSONAL AGENT NOTIFICATION PREFERENCES ("MY ACCOUNT") */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {activeTab === 'personal_preferences' && (
        <div className="space-y-5">
          <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
            <div className="border-b border-border pb-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                  <User className="size-4 text-sky-500" /> Personal Notification &amp; Warning Subscriptions
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Agents can personalize their delivery channels (Email, Toast, Siren Chime, Chat Spaces) for every system notification and warning rule.
                </p>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground font-semibold">Agent Profile:</span>
                <span className="px-2.5 py-1 rounded bg-muted font-mono font-semibold text-foreground border border-border">
                  {activeAgentEmail}
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-border bg-muted/40 font-semibold text-muted-foreground">
                    <th className="p-3">Rule &amp; Trigger Type</th>
                    <th className="p-3 w-32">Severity</th>
                    <th className="p-3 w-28 text-center">Email</th>
                    <th className="p-3 w-28 text-center">In-App Toast</th>
                    <th className="p-3 w-28 text-center">Audio Chime</th>
                    <th className="p-3 w-28 text-center">Chat Webhook</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rules.map(rule => {
                    const prefs = agentPreferences[rule.id] || { email: true, in_app: true, sound: true, chat_webhook: true };
                    const isWarning = rule.eventTrigger.includes('warning') || rule.eventTrigger.includes('breached') || rule.warningSeverity === 'critical' || rule.warningSeverity === 'urgent';
                    const severity = SEVERITY_CONFIG[rule.warningSeverity || (isWarning ? 'urgent' : 'info')];

                    return (
                      <tr key={rule.id} className="hover:bg-muted/20">
                        <td className="p-3">
                          <div className="font-semibold text-foreground flex items-center gap-1.5">
                            {isWarning && <AlertTriangle className="size-3.5 text-rose-500" />}
                            {rule.name}
                          </div>
                          <div className="text-xs text-muted-foreground line-clamp-1">{rule.description}</div>
                        </td>
                        <td className="p-3">
                          <span className={cn("px-1.5 py-0.5 rounded text-xs font-semibold border", severity.badge)}>
                            {rule.warningSeverity?.toUpperCase() || (isWarning ? 'URGENT' : 'INFO')}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleTogglePersonalPref(rule.id, 'email')}
                            className={cn(
                              "px-2 py-0.5 rounded text-xs font-semibold border transition-all cursor-pointer",
                              prefs.email ? "bg-sky-50 text-sky-700 border-sky-300 dark:bg-sky-950/50 dark:text-sky-300" : "bg-muted text-muted-foreground border-transparent opacity-40"
                            )}
                          >
                            {prefs.email ? 'Subscribed' : 'Off'}
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleTogglePersonalPref(rule.id, 'in_app')}
                            className={cn(
                              "px-2 py-0.5 rounded text-xs font-semibold border transition-all cursor-pointer",
                              prefs.in_app ? "bg-indigo-50 text-indigo-700 border-indigo-300 dark:bg-indigo-950/50 dark:text-indigo-300" : "bg-muted text-muted-foreground border-transparent opacity-40"
                            )}
                          >
                            {prefs.in_app ? 'Subscribed' : 'Off'}
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleTogglePersonalPref(rule.id, 'sound')}
                            className={cn(
                              "px-2 py-0.5 rounded text-xs font-semibold border transition-all cursor-pointer",
                              prefs.sound ? "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/50 dark:text-amber-300" : "bg-muted text-muted-foreground border-transparent opacity-40"
                            )}
                          >
                            {prefs.sound ? 'Chime ON' : 'Muted'}
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleTogglePersonalPref(rule.id, 'chat_webhook')}
                            className={cn(
                              "px-2 py-0.5 rounded text-xs font-semibold border transition-all cursor-pointer",
                              prefs.chat_webhook ? "bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-950/50 dark:text-purple-300" : "bg-muted text-muted-foreground border-transparent opacity-40"
                            )}
                          >
                            {prefs.chat_webhook ? 'Space Alert' : 'Off'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* TAB 3: MULTI-CHANNEL DISPATCH SIMULATOR */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {activeTab === 'simulator' && (
        <div className="space-y-4 text-xs">
          <div className="rounded-xl border border-indigo-200 dark:border-indigo-900 bg-card p-5 shadow-xs space-y-4">
            <div className="border-b border-border pb-3 flex items-center justify-between">
              <div>
                <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                  <Play className="size-4 text-indigo-600" /> Multi-Channel Live Notification &amp; Warning Simulator
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Simulate live dispatch across Email, In-App Floating Toast, Sound Alerts, and Google Chat cards with token interpolation.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  playSampleSound(currentRule.template.soundChime || (isWarningTrigger ? 'urgent_siren' : 'standard_chime'));
                  toast.success(`Dispatched simulated alert for rule "${currentRule.name}"!`);
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-indigo-600 text-white font-semibold shadow-xs hover:bg-indigo-700 cursor-pointer"
              >
                <Send className="size-3.5" /> Trigger Test Event
              </button>
            </div>

            {/* CHANNEL PREVIEW TABS */}
            <div className="flex items-center gap-2 border-b border-border pb-2">
              {[
                { key: 'email' as const, label: 'Email Dispatch Preview', icon: Mail },
                { key: 'in_app' as const, label: 'In-App Toast Banner', icon: Bell },
                { key: 'chat_webhook' as const, label: 'Chat Webhook Card', icon: MessageSquare },
                { key: 'sms' as const, label: 'SMS Push Notification', icon: Smartphone },
              ].map(chan => {
                const IconComp = chan.icon;
                const isCurrent = simPreviewChannel === chan.key;
                return (
                  <button
                    key={chan.key}
                    type="button"
                    onClick={() => setSimPreviewChannel(chan.key)}
                    className={cn(
                      "px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all cursor-pointer",
                      isCurrent
                        ? "bg-card text-foreground font-semibold border border-border shadow-xs"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <IconComp className="size-3.5" />
                    <span>{chan.label}</span>
                  </button>
                );
              })}
            </div>

            {/* PREVIEW CONTAINER */}
            <div className="rounded-xl border border-border bg-muted/20 p-5">
              {simPreviewChannel === 'email' && (
                <div className="max-w-xl mx-auto rounded-lg border border-border bg-card p-5 shadow-xs space-y-3 font-sans">
                  <div className="border-b border-border pb-2">
                    <div className="text-xs text-muted-foreground">From: <span className="text-foreground font-semibold">{activeSender.formatted}</span></div>
                    <div className="text-xs text-muted-foreground">To: <span className="text-foreground font-semibold">girikumaran@trustybytes.in</span></div>
                    <div className="text-xs font-semibold text-foreground mt-1.5">
                      Subject: {currentRule.template.subject.replace(/{ticket_id}/g, '10042').replace(/{subject}/g, 'PostgreSQL Disconnect Error').replace(/{priority}/g, 'Urgent')}
                    </div>
                  </div>
                  <div className="text-xs text-foreground whitespace-pre-wrap leading-relaxed">
                    {currentRule.template.bodyText
                      .replace(/{ticket_id}/g, '10042')
                      .replace(/{subject}/g, 'PostgreSQL Disconnect Error')
                      .replace(/{requester_name}/g, 'Sarah Connor')
                      .replace(/{assignee_name}/g, 'Girikumaran')
                      .replace(/{priority}/g, 'Urgent')
                      .replace(/{queue_name}/g, 'Support Team')
                      .replace(/{sla_target_time}/g, '2 hours')
                      .replace(/{sla_time_remaining}/g, '14 mins')
                      .replace(/{impacted_service}/g, 'Corporate VPN')
                      .replace(/{status}/g, 'In Progress')
                      .replace(/{company_name}/g, 'TrustyBytes')}
                  </div>
                </div>
              )}

              {simPreviewChannel === 'in_app' && (
                <div className="max-w-md mx-auto p-4 rounded-xl border border-border bg-card shadow-lg flex items-start gap-3">
                  <div className={cn(
                    "size-8 rounded-full flex items-center justify-center flex-shrink-0",
                    isWarningTrigger ? "bg-rose-500/10 text-rose-600" : "bg-indigo-500/10 text-indigo-600"
                  )}>
                    {isWarningTrigger ? <AlertTriangle className="size-4" /> : <BellRing className="size-4" />}
                  </div>
                  <div>
                    <div className="font-semibold text-foreground text-xs flex items-center gap-1.5">
                      {currentRule.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Ticket TB#10042 ({currentRule.eventTrigger.replace(/_/g, ' ')}) in Support Team requires urgent agent triage.
                    </div>
                    <span className="text-xs text-primary font-semibold mt-1 inline-block">Just now • TeamOne Realtime Team</span>
                  </div>
                </div>
              )}

              {simPreviewChannel === 'chat_webhook' && (
                <div className="max-w-md mx-auto p-4 rounded-xl border border-border bg-card shadow-xs space-y-2.5">
                  <div className="flex items-center gap-2">
                    <div className="size-6 rounded bg-emerald-600 text-white flex items-center justify-center font-semibold text-xs">
                      T1
                    </div>
                    <span className="font-semibold text-xs text-foreground">TeamOne Support Bot</span>
                    <span className="text-xs text-muted-foreground font-mono">APP • 11:20 AM</span>
                  </div>
                  <div className={cn(
                    "p-3 rounded-lg border-l-4 bg-muted/30 text-xs space-y-1",
                    isWarningTrigger ? "border-l-rose-600" : "border-l-primary"
                  )}>
                    <div className="font-semibold text-foreground">{currentRule.template.chatCardTitle || currentRule.name}</div>
                    <div className="text-muted-foreground text-xs">Ticket: <strong className="text-foreground">TB#10042</strong> — Priority: <span className="text-rose-600 font-semibold">Urgent P1</span></div>
                    <div className="text-muted-foreground text-xs">Queue: <strong className="text-foreground">Support Team</strong></div>
                    <div className="text-muted-foreground text-xs">Time Remaining: <span className="text-amber-600 font-semibold">14 mins</span></div>
                  </div>
                </div>
              )}

              {simPreviewChannel === 'sms' && (
                <div className="max-w-xs mx-auto p-4 rounded-2xl border border-border bg-card shadow-sm space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase">SMS Push Alert (+91 98765 43210)</div>
                  <div className="p-2.5 rounded-lg bg-muted text-xs text-foreground font-mono">
                    [TeamOne Alert] TB#10042 ({currentRule.eventTrigger}): Action required. Check helpdesk queue immediately.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* DRAWER: CREATE NEW NOTIFICATION / WARNING RULE */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {showAddRuleModal && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in">
          <div className="w-full max-w-lg bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                <Plus className="size-4 text-primary" /> Create Notification or Warning Rule
              </h3>
              <button onClick={() => setShowAddRuleModal(false)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleAddRuleSubmit} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div>
                <label className="block font-semibold text-muted-foreground mb-1">Rule Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. VIP Major Incident Instant Escalation"
                  value={newRuleForm.name}
                  onChange={(e) => setNewRuleForm({ ...newRuleForm, name: e.target.value })}
                  className="w-full px-3 py-1.5 bg-background border border-input rounded-md text-xs font-semibold"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-muted-foreground mb-1">Event Trigger Type</label>
                  <select
                    value={newRuleForm.eventTrigger}
                    onChange={(e) => {
                      const trigger = e.target.value as EventTriggerType;
                      const info = TRIGGER_LABELS[trigger];
                      setNewRuleForm({
                        ...newRuleForm,
                        eventTrigger: trigger,
                        warningSeverity: info?.defaultSeverity || 'info'
                      });
                    }}
                    className="w-full px-3 py-1.5 bg-background border border-input rounded-md text-xs font-semibold"
                  >
                    <option value="sla_warning">⚠️ SLA Warning Threshold</option>
                    <option value="sla_breached">🚨 SLA Target Breached</option>
                    <option value="unassigned_warning">⚠️ Unassigned Queue Warning</option>
                    <option value="inactivity_warning">⏳ Stale / Inactivity Warning</option>
                    <option value="urgent_priority_warning">🔥 High Priority P1 Alert</option>
                    <option value="ticket_created">📩 Ticket Created / Received</option>
                    <option value="ticket_assigned">👤 Ticket Assigned to Agent</option>
                    <option value="customer_replied">💬 Customer Replied</option>
                    <option value="approval_requested">📋 Approval Requested</option>
                    <option value="ticket_resolved">✅ Ticket Resolved</option>
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-muted-foreground mb-1">Warning Severity</label>
                  <select
                    value={newRuleForm.warningSeverity}
                    onChange={(e) => setNewRuleForm({ ...newRuleForm, warningSeverity: e.target.value as WarningSeverity })}
                    className="w-full px-3 py-1.5 bg-background border border-input rounded-md text-xs font-semibold"
                  >
                    <option value="info">Info Notification</option>
                    <option value="low">Low Warning (Amber)</option>
                    <option value="urgent">Urgent Warning (Orange)</option>
                    <option value="critical">Critical Siren (Red)</option>
                  </select>
                </div>
              </div>

              {/* Threshold for Warnings */}
              <div className="p-3 rounded-lg border border-border bg-muted/20 grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-muted-foreground mb-1">Threshold Window</label>
                  <input
                    type="number"
                    min={1}
                    value={newRuleForm.warningThresholdValue}
                    onChange={(e) => setNewRuleForm({ ...newRuleForm, warningThresholdValue: parseInt(e.target.value) || 15 })}
                    className="w-full px-2.5 py-1 bg-background border border-input rounded-md text-xs font-mono"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-muted-foreground mb-1">Threshold Unit</label>
                  <select
                    value={newRuleForm.warningThresholdUnit}
                    onChange={(e) => setNewRuleForm({ ...newRuleForm, warningThresholdUnit: e.target.value as any })}
                    className="w-full px-2.5 py-1 bg-background border border-input rounded-md text-xs font-semibold"
                  >
                    <option value="minutes">Minutes before breach</option>
                    <option value="hours">Hours inactive</option>
                    <option value="percent_sla">% SLA elapsed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-muted-foreground mb-1">Subject Template</label>
                <input
                  type="text"
                  required
                  value={newRuleForm.subject}
                  onChange={(e) => setNewRuleForm({ ...newRuleForm, subject: e.target.value })}
                  className="w-full px-3 py-1.5 bg-background border border-input rounded-md text-xs font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-muted-foreground mb-1">Body Text</label>
                <textarea
                  rows={3}
                  value={newRuleForm.bodyText}
                  onChange={(e) => setNewRuleForm({ ...newRuleForm, bodyText: e.target.value })}
                  className="w-full px-3 py-1.5 bg-background border border-input rounded-md text-xs font-mono"
                />
              </div>

              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddRuleModal(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                >
                  Create Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
